import { inject, Injectable, input } from '@angular/core';
import { Router, NavigationEnd, ActivatedRoute, ParamMap, Params } from '@angular/router';
import { Location } from '@angular/common';
import { BehaviorSubject, Observable, combineLatest } from 'rxjs';
import { filter, map, shareReplay, distinctUntilChanged } from 'rxjs/operators';
import { AllLocalStorageService } from './all-local-storage.service';

export interface UrlInfo {
  url: string;
  params: Params;
  queryParams: Params;
  fragment: string | null;
  data: any;
  timestamp: Date;
}

export interface NavigationHistory {
  current: UrlInfo;
  previous: UrlInfo | null;
  history: UrlInfo[];
}

@Injectable({
  providedIn: 'root'
})
export class NavigationService {
  private navigationHistory: NavigationHistory = {
    current: null,
    previous: null,
    history: []
  };

  private navigationHistory$ = new BehaviorSubject<NavigationHistory>(this.navigationHistory);
  private maxHistorySize = 50; // Configurable history size

  private _localStorageService = inject(AllLocalStorageService);
  constructor(
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private location: Location
  ) {
    this.initializeService();
  }

  private initializeService(): void {
    // Listen to router events for successful navigation
    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        map(event => event as NavigationEnd)
      )
      .subscribe((event: NavigationEnd) => {
        this.updateHistory(event);
      });

    // Initialize with current URL
    this.updateHistory();
  }

  private updateHistory(navigationEnd?: NavigationEnd): void {
    const currentUrlInfo = this.getCurrentUrlInfo();
    
    if (this.navigationHistory.current) {
      this.navigationHistory.previous = { ...this.navigationHistory.current };
    }
    
    this.navigationHistory.current = currentUrlInfo;
    this.navigationHistory.history.unshift(currentUrlInfo);
    
    // Maintain history size limit
    if (this.navigationHistory.history.length > this.maxHistorySize) {
      this.navigationHistory.history.pop();
    }
    
    this.navigationHistory$.next({ ...this.navigationHistory });
  }

  private getCurrentUrlInfo(): UrlInfo {
    let route = this.activatedRoute;
    while (route.firstChild) {
      route = route.firstChild;
    }

    const params = route.snapshot.params;
    const queryParams = route.snapshot.queryParams;
    const fragment = route.snapshot.fragment;
    const data = route.snapshot.data;

    return {
      url: this.router.url,
      params,
      queryParams,
      fragment,
      data,
      timestamp: new Date()
    };
  }

  // Observable for URL changes
  getUrlChanges$(): Observable<string> {
    return this.router.events.pipe(
      filter(event => event instanceof NavigationEnd),
      map(event => (event as NavigationEnd).url),
      distinctUntilChanged(),
      shareReplay(1)
    );
  }

  // Observable for current URL info
  getCurrentUrlInfo$(): Observable<UrlInfo> {
    return this.navigationHistory$.pipe(
      map(history => history.current),
      filter(current => !!current),
      distinctUntilChanged(),
      shareReplay(1)
    );
  }

  // Observable for navigation history
  getNavigationHistory$(): Observable<NavigationHistory> {
    return this.navigationHistory$.asObservable();
  }

  // Get current URL
  getCurrentUrl(): string {
    return this.router.url;
  }

  // Get current URL without query params
  getCurrentUrlWithoutQueryParams(): string {
    return this.location.path(false);
  }

  // Get current route params
  getCurrentParams(): Observable<Params> {
    return this.activatedRoute.params;
  }

  // Get current query params
  getCurrentQueryParams(): Observable<Params> {
    return this.activatedRoute.queryParams;
  }

  // Get specific route param
  getRouteParam(paramName: string): Observable<string | null> {
    return this.activatedRoute.params.pipe(
      map(params => params[paramName] || null)
    );
  }

  // Get specific query param
  getQueryParam(paramName: string): Observable<string | null> {
    return this.activatedRoute.queryParams.pipe(
      map(params => params[paramName] || null)
    );
  }

  // Get current fragment
  getCurrentFragment(): Observable<string | null> {
    return this.activatedRoute.fragment;
  }

  // Get current route data
  getCurrentRouteData(): Observable<any> {
    return this.activatedRoute.data;
  }

  // Get param map (useful for complex parameter handling)
  getCurrentParamMap(): Observable<ParamMap> {
    return this.activatedRoute.paramMap;
  }

  // Get query param map
  getCurrentQueryParamMap(): Observable<ParamMap> {
    return this.activatedRoute.queryParamMap;
  }

  // Get combined route and query params
  getAllParams(): Observable<Params> {
    return combineLatest([
      this.activatedRoute.params,
      this.activatedRoute.queryParams
    ]).pipe(
      map(([routeParams, queryParams]) => ({
        ...routeParams,
        ...queryParams
      }))
    );
  }

  // Get full URL with protocol and host
  getFullUrl(): string {
    return window.location.href;
  }

  // Get base URL
  getBaseUrl(): string {
    return window.location.origin;
  }

  // Get URL pathname
  getPathname(): string {
    return window.location.pathname;
  }

  // Get URL search (query string)
  getSearch(): string {
    return window.location.search;
  }

  // Get URL hash
  getHash(): string {
    return window.location.hash;
  }

  // Get previous URL
  getPreviousUrl(): string | null {
    // If current URL === previous URL, get url from localStorage
          console.log('Previous URL from localStorage:', this.navigationHistory.previous?.url);

    if (this.navigationHistory.previous?.url === this.getCurrentUrl()) {
      const previousUrl = this.getUrlFromLocalStorage();
      console.log('Previous URL from localStorage:', previousUrl);
      if (previousUrl) {
        return previousUrl;
      }
    }

    return this.navigationHistory.previous?.url || null;
  }

  // Get URL history
  getUrlHistory(): UrlInfo[] {
    return [...this.navigationHistory.history];
  }

  // Check if we can go back
  canGoBack(): boolean {
    return this.navigationHistory.history.length > 1;
  }

  // Navigate back in history
  goBack(): void {
    if (this.canGoBack()) {
      this.location.back();
    }
  }

  // Navigate forward in history
  goForward(): void {
    this.location.forward();
  }

  // Set maximum history size
  setMaxHistorySize(size: number): void {
    this.maxHistorySize = size;
    if (this.navigationHistory.history.length > size) {
      this.navigationHistory.history = this.navigationHistory.history.slice(0, size);
      this.navigationHistory$.next({ ...this.navigationHistory });
    }
  }

  // Clear navigation history
  clearHistory(): void {
    this.navigationHistory.history = this.navigationHistory.current ? [this.navigationHistory.current] : [];
    this.navigationHistory$.next({ ...this.navigationHistory });
  }

  // Check if current URL matches pattern
  isCurrentUrl(pattern: string | RegExp): boolean {
    const currentUrl = this.getCurrentUrl();
    if (typeof pattern === 'string') {
      return currentUrl === pattern;
    }
    return pattern.test(currentUrl);
  }

  // Check if query param exists
  hasQueryParam(paramName: string): boolean {
    return this.activatedRoute.snapshot.queryParams.hasOwnProperty(paramName);
  }

  // Check if route param exists
  hasRouteParam(paramName: string): boolean {
    return this.activatedRoute.snapshot.params.hasOwnProperty(paramName);
  }

  // Get URL segments
  getUrlSegments(): string[] {
    return this.router.url.split('/').filter(segment => segment !== '');
  }

  // Get active route configuration
  getActiveRouteConfig(): any {
    let route = this.activatedRoute;
    while (route.firstChild) {
      route = route.firstChild;
    }
    return route.routeConfig;
  }

  // Subscribe to parameter changes for a specific route
  subscribeToParamChanges(callback: (params: Params) => void): Observable<Params> {
    return this.activatedRoute.params.pipe(
      distinctUntilChanged()
    );
  }

  // Subscribe to query parameter changes
  subscribeToQueryParamChanges(callback: (params: Params) => void): Observable<Params> {
    return this.activatedRoute.queryParams.pipe(
      distinctUntilChanged()
    );
  }

  // Get resolved data from route
  getResolvedData(): Observable<any> {
    return this.activatedRoute.data;
  }

  // Check if navigation is in progress
  isNavigating(): Observable<boolean> {
    return this.router.events.pipe(
      map(event => {
        return event.constructor.name === 'NavigationStart';
      }),
      distinctUntilChanged(),
      shareReplay(1)
    );
  }

  // Save URL to localStorage
  saveCurrentUrlToLocalStorage(): void {
    const currentUrl = this.getCurrentUrl();
    const complement = this.getRouteParam('complement') || 'pasajes';
    this._localStorageService.guardarUrlVolver(currentUrl, complement);
  }

  getUrlFromLocalStorage(): string | null {
    const complement = this.getRouteParam('complement') || 'pasajes';
    return this._localStorageService.obtenerUrlVolver(complement) || null;
  }
}